(() => {
  const config = document.querySelector('[data-sd-exact-search-config]');
  const core = window.SDSearchCore;
  if (!config || !core) return;
  const searchPath = config.dataset.searchUrl;
  const root = config.dataset.rootUrl.replace(/\/$/, '');
  const preview = config.dataset.previewTheme;
  const groups = core.roles;
  const cache = new Map();
  const bypass = new WeakSet();
  const pending = new WeakSet();
  const targets = new Map();
  let catalogPromise;
  const withPreview = url => {
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  function catalog() {
    if (!catalogPromise) {
      catalogPromise = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        try {
          const response = await fetch(config.dataset.identifiersUrl, {signal: controller.signal});
          if (!response.ok) throw new Error('Catalog index unavailable');
          const data = await response.json();
          if (![1, 2].includes(data.version) || !Array.isArray(data.products)) throw new Error('Invalid catalog index');
          return {...data, identifiers: core.identifiers(data)};
        } finally {
          clearTimeout(timer);
        }
      })();
      catalogPromise.catch(() => { catalogPromise = null; });
    }
    return catalogPromise;
  }
  function destination(match) {
    const handle = root === '/fr' ? match.product.fr_handle : match.product.handle;
    const url = new URL(`${root}/products/${encodeURIComponent(handle)}`, location.origin);
    if (match.variant) url.searchParams.set('variant', match.variant.id);
    return withPreview(url).href;
  }
  async function nativeExact(query) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      const url = withPreview(new URL(searchPath, location.origin));
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('view', 'sd-exact');
      const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal});
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = JSON.parse(doc.querySelector('[data-sd-exact-data]')?.textContent || '{}');
      if (!data.complete || !data.matches?.length) return null;
      const matches = ['sku', 'barcode', 'model'].map(kind => data.matches.filter(match => match.kind === kind)).find(list => list.length);
      if (new Set(matches.map(match => match.id)).size !== 1) return null;
      const variants = new Set(matches.map(match => match.url));
      const target = variants.size === 1 ? matches[0].url : matches[0].parent;
      const result = new URL(target, location.origin);
      if (result.origin !== location.origin || !result.pathname.startsWith(`${root}/products/`)) return null;
      for (const key of ['_pos', '_sid', '_ss']) result.searchParams.delete(key);
      return withPreview(result).href;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
  function targetFor(query) {
    const key = core.normalize(query);
    if (!key || key.length > 80) return Promise.resolve(null);
    if (!targets.has(key)) {
      targets.set(key, (async () => {
        try {
          const data = await catalog();
          const exact = core.exact(data.identifiers, query);
          if (exact) return destination(exact);
          const hasAnyMatch = data.identifiers.sku.has(key) || data.identifiers.model.has(key) || data.identifiers.alias.has(key) || data.identifiers.barcode.has(core.barcode(query));
          if (hasAnyMatch || !/\d/.test(query) || !(/^[\p{L}\p{N}._\/-]+$/u.test(query) || core.barcode(query))) return null;
        } catch {}
        return nativeExact(query);
      })());
    }
    return targets.get(key);
  }
  async function searchDocument(url, signal) {
    const key = url.href;
    if (!cache.has(key)) {
      const promise = (async () => {
        const response = await fetch(url, {credentials: 'same-origin', signal});
        if (!response.ok) throw new Error('Search unavailable');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      })();
      cache.set(key, promise);
      promise.catch(() => cache.delete(key));
      if (cache.size > 36) cache.delete(cache.keys().next().value);
    }
    return cache.get(key);
  }
  async function matchesFor(query, filters, signal) {
    const source = async number => {
      const url = withPreview(new URL(searchPath, location.origin));
      for (const [key, value] of filters || []) {
        if (key.startsWith('filter.') || key.startsWith('options[')) url.searchParams.append(key, value);
      }
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('view', 'sd-priority-matches');
      url.searchParams.set('sort_by', 'relevance');
      url.searchParams.set('page', String(number));
      const doc = await searchDocument(url, signal);
      const data = JSON.parse(doc.querySelector('[data-sd-priority-matches]')?.textContent || '{}');
      if (!Array.isArray(data.matches) || !Number.isInteger(data.pages) || data.pages > 40) throw new Error('Search response unavailable');
      return data;
    };
    const first = await source(1);
    const later = await Promise.all(Array.from({length: Math.max(0, first.pages - 1)}, (_, index) => source(index + 2)));
    return core.rankMatches([first, ...later].flatMap(result => result.matches));
  }
  async function renderProducts(matches, query, mode, signal) {
    let data;
    try { data = await catalog(); } catch {}
    const products = new Map((data?.products || []).map(product => [product.id, product]));
    const exact = data && core.exact(data.identifiers, query);
    const references = matches.map(match => {
      const handle = products.get(match.id)?.handle || match.handle;
      if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) throw new Error('Invalid product reference');
      return handle + (exact?.product.id === match.id && exact.variant ? `@${exact.variant.id}` : '');
    });
    const requests = [];
    for (let offset = 0; offset < references.length; offset += 20) {
      const url = withPreview(new URL(searchPath, location.origin));
      url.searchParams.set('q', references.slice(offset, offset + 20).join('|'));
      url.searchParams.set('type', 'product');
      url.searchParams.set('view', `sd-priority-${mode}`);
      requests.push(searchDocument(url, signal));
    }
    const docs = await Promise.all(requests);
    return {items: docs.flatMap(doc => [...doc.querySelectorAll('template[data-sd-priority-item]')]), footer: docs[0]?.querySelector('[data-sd-priority-footer]')};
  }
  function decoratePreview(element) {
    if (!preview) return;
    for (const anchor of element.querySelectorAll('a[href]')) {
      const url = new URL(anchor.href, location.origin);
      if (url.origin === location.origin) anchor.href = withPreview(url).href;
    }
  }
  async function suggestions(query, productType, signal) {
    let matches = await matchesFor(query, new URLSearchParams({'options[prefix]': 'last'}), signal);
    let data;
    try { data = await catalog(); } catch {}
    const exact = data && core.exact(data.identifiers, query);
    if (exact && !matches.some(match => match.id === exact.product.id)) {
      matches.unshift({id: exact.product.id, handle: exact.product.handle, tier: groups.indexOf(exact.product.role), type: exact.product.type});
    }
    if (productType) matches = matches.filter(match => match.type === productType);
    const selected = matches.slice(0, 8);
    const result = await renderProducts(selected, query, 'suggest', signal);
    const items = new Map(result.items.map(item => [item.dataset.productId, item]));
    const fragment = document.createElement('div');
    groups.forEach((role, tier) => {
      const group = selected.filter(match => match.tier === tier && items.has(match.id));
      if (!group.length) return;
      const heading = document.createElement('p');
      heading.className = 'search-bar__result-category';
      heading.textContent = config.dataset[`${role}Label`];
      const list = document.createElement('div');
      list.className = 'search-bar__result-products';
      for (const match of group) list.append(items.get(match.id).content.cloneNode(true));
      fragment.append(heading, list);
    });
    if (selected.length && result.footer) fragment.append(result.footer.content.cloneNode(true));
    else {
      const empty = document.createElement('div');
      empty.className = 'search-bar__empty-state';
      const title = document.createElement('p');
      title.className = 'heading h4';
      title.textContent = config.dataset.emptyLabel;
      empty.append(title);
      fragment.append(empty);
    }
    decoratePreview(fragment);
    return fragment.innerHTML;
  }
  window.SDCatalogSearch = {suggestions};
  document.addEventListener('focusin', event => {
    if (event.target.matches('input[name="q"],.search-bar__input')) catalog().catch(() => {});
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[role="search"],.search-bar')) return;
    if (bypass.has(form)) { bypass.delete(form); return; }
    const input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending.has(form)) return;
    pending.add(form);
    const original = input.value;
    form.setAttribute('aria-busy', 'true');
    const target = await targetFor(original);
    form.removeAttribute('aria-busy');
    pending.delete(form);
    if (input.value !== original || !form.isConnected) return;
    if (target) location.assign(target);
    else { bypass.add(form); form.requestSubmit(); }
  }, true);
  const params = new URLSearchParams(location.search);
  const isSearch = location.pathname.replace(/\/$/, '') === searchPath.replace(/\/$/, '');
  const filtered = [...params.keys()].some(key => key.startsWith('filter.'));
  if (isSearch && params.get('q') && !filtered && !params.has('page') && !params.has('sort_by') && !params.has('view')) {
    const current = location.href;
    targetFor(params.get('q')).then(target => { if (target && location.href === current) location.replace(target); });
  }
  const page = document.querySelector('[data-sd-search-page][data-sd-priority-enabled="true"]');
  const grid = page?.querySelector('.sd-search-grid');
  if (grid && isSearch && params.get('q')) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    grid.setAttribute('aria-busy', 'true');
    page.dataset.sdRankingState = 'loading';
    (async () => {
      const query = params.get('q');
      const matches = await matchesFor(query, params, controller.signal);
      const number = Math.max(1, Number(params.get('page')) || 1);
      const size = Number(page.dataset.pageSize) || 24;
      const selected = matches.slice((number - 1) * size, number * size);
      const result = await renderProducts(selected, query, 'cards', controller.signal);
      const cards = result.items;
      page.dataset.sdRankedCount = String(matches.length);
      if (cards.length && !controller.signal.aborted) {
        const content = document.createDocumentFragment();
        cards.forEach((item, index) => {
          const card = item.content.cloneNode(true);
          const label = card.querySelector('.sd-result-card__index');
          if (label) label.textContent = String((number - 1) * size + index + 1).padStart(2, '0');
          decoratePreview(card);
          content.append(card);
        });
        grid.replaceChildren(content);
        const count = page.querySelector('.sd-search-result-count');
        if (count) count.textContent = matches.length === 1 ? page.dataset.countOne : page.dataset.countMany.replace('__COUNT__', matches.length.toLocaleString(document.documentElement.lang));
        const pagination = page.querySelector('[data-sd-search-pagination]');
        const pages = Math.ceil(matches.length / size);
        if (pagination && pages > 1) {
          const pager = page.querySelector('[data-sd-search-pagination-template]').content.cloneNode(true);
          const href = target => { const url = withPreview(new URL(location.href)); url.searchParams.set('page', String(target)); return url.href; };
          const previous = pager.querySelector('[data-sd-previous]');
          if (number > 1) previous.href = href(number - 1); else previous.remove();
          const next = pager.querySelector('[data-sd-next]');
          if (number < pages) next.href = href(number + 1); else next.remove();
          pager.querySelector('[data-sd-page-count]').textContent = page.dataset.pageCount.replace('__CURRENT__', number).replace('__PAGES__', pages);
          const nav = pager.querySelector('[data-sd-page-links]');
          const numbers = [...new Set([1, pages, ...Array.from({length: 5}, (_, index) => number - 2 + index)])].filter(value => value > 0 && value <= pages).sort((a, b) => a - b);
          let last = 0;
          for (const value of numbers) {
            if (last && value - last > 1) { const gap = document.createElement('span'); gap.className = 'pagination__nav-item'; gap.textContent = '…'; nav.append(gap); }
            const link = document.createElement(value === number ? 'span' : 'a');
            link.className = 'pagination__nav-item' + (value === number ? ' is-active' : ' link');
            link.textContent = value;
            if (value === number) link.setAttribute('aria-current', 'page');
            else { link.href = href(value); link.title = page.dataset.pageTitle.replace('__PAGE__', value); }
            nav.append(link);
            last = value;
          }
          pagination.replaceChildren(pager);
        } else if (pagination) pagination.replaceChildren();
        page.dataset.sdRankingState = 'ready';
      } else page.dataset.sdRankingState = 'fallback';
    })().catch(() => { page.dataset.sdRankingState = 'fallback'; }).finally(() => {
      clearTimeout(timer);
      grid.removeAttribute('aria-busy');
    });
    window.addEventListener('pagehide', () => controller.abort(), {once: true});
  }
})();
